import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Turnstile } from "@marsidev/react-turnstile";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import HeroVisual from "@/components/HeroVisual";
import { SITE } from "@/data/site";
import { Phone, Mail, MapPin, Send, CheckCircle2, AlertCircle, MessageCircle, MessageSquare, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENV_TURNSTILE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [siteKey, setSiteKey] = useState(ENV_TURNSTILE_KEY);
  const turnstileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/site-config`).then(r => {
      if (r.data?.turnstile_site_key) setSiteKey(r.data.turnstile_site_key);
    }).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", msg: "Lütfen ad, e-posta ve mesaj alanlarını doldurun." });
      return;
    }
    if (siteKey && !turnstileToken) {
      setStatus({ state: "error", msg: "Lütfen güvenlik doğrulamasını tamamlayın." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/contact`, { ...form, turnstile_token: turnstileToken });
      if (res.data?.success) {
        setStatus({ state: "success", msg: res.data.message || "Mesajınız başarıyla iletildi." });
        setForm({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
      } else {
        setStatus({ state: "error", msg: "Mesaj gönderilemedi. Lütfen tekrar deneyin." });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Bir hata oluştu. Lütfen tekrar deneyin.";
      setStatus({ state: "error", msg: typeof msg === "string" ? msg : "Bir hata oluştu." });
    } finally {
      // Tek kullanımlık token; her gönderim sonrası yenile
      setTurnstileToken("");
      turnstileRef.current?.reset();
    }
  };

  return (
    <>
      <SEO title="İletişim — Pratik Teknoloji" description="Pratik Bilişim Teknolojileri A.Ş. ile iletişime geçin. Telefon, e-posta, adres ve iletişim formu." />

      <PageHero
        badge="İletişim"
        title={<>Bize</>}
        titleAlt="ulaşın."
        subtitle="İşletmenizin teknoloji altyapısını güçlendirmek, teknik destek almak veya çözümlerimiz hakkında detaylı bilgi edinmek için bizimle iletişime geçebilirsiniz. Tüm talepleriniz uzman ekibimiz tarafından en kısa sürede değerlendirilir."
        rightSlot={
          <HeroVisual
            accentColor="#FF3B30"
            center={{ icon: <MessageSquare size={36} strokeWidth={1.6} />, topLabel: "7/24", bottomLabel: "Ulaşılabilir" }}
            cards={[
              { position: "tr", rotate: 4, delay: 0.4, color: "#34C759", icon: <Clock size={16} />, label: "Cevap", value: "30 dk", sub: "ortalama yanıt süresi" },
              { position: "ml", rotate: -5, delay: 0.55, color: "#25D366", icon: <MessageCircle size={16} />, label: "WhatsApp", value: "Anlık", sub: "canlı destek hattı" },
              { position: "br", rotate: 3, delay: 0.7, color: "#007AFF", icon: <MapPin size={16} />, label: "Lokasyon", value: "Çanakkale", sub: "merkez ofis" },
            ]}
          />
        }
      />

      <section className="pb-24 md:pb-32 bg-[#0A0A0A] border-t border-white/10 pt-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Info */}
            <aside className="lg:col-span-4 space-y-4">
              <InfoCard icon={<Phone size={22} strokeWidth={1.8} />} label="Telefon" value={SITE.phone} href={`tel:${SITE.phoneRaw}`} />
              <InfoCard icon={<Mail size={22} strokeWidth={1.8} />} label="E-posta" value={SITE.email} href={`mailto:${SITE.email}`} />
              <InfoCard icon={<MapPin size={22} strokeWidth={1.8} />} label="Adres" value={<>{SITE.address.line1}<br/>{SITE.address.line2}<br/>{SITE.address.city} – {SITE.address.zip}</>} />

              <div className="relative rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] border border-white/10 text-white p-8 overflow-hidden">
                <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#25D366]/25 rounded-full blur-3xl"></div>
                <div className="relative">
                  <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#25D366] mb-3">WhatsApp</div>
                  <div className="font-display text-2xl font-bold">Anlık cevap ister misiniz?</div>
                  <a href={`https://wa.me/${SITE.whatsappRaw}`} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">
                    <MessageCircle size={18} /> WhatsApp'tan Yaz
                  </a>
                </div>
              </div>
            </aside>

            {/* Form */}
            <div className="lg:col-span-8">
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-3xl bg-white/[0.03] border border-white/10 p-8 md:p-12"
                noValidate
              >
                {/* Honeypot */}
                <input type="text" name="website" value={form.website} onChange={update("website")} tabIndex={-1} autoComplete="off"
                  style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }} aria-hidden="true" />

                <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#FF3B30] mb-3">Mesaj Bırakın</div>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mb-10">Birlikte ne yapabileceğimizi konuşalım.</h2>

                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Ad Soyad *" testId="contact-name">
                    <input required value={form.name} onChange={update("name")} className="input-dark" placeholder="Adınız ve soyadınız" />
                  </Field>
                  <Field label="E-posta *" testId="contact-email">
                    <input required type="email" value={form.email} onChange={update("email")} className="input-dark" placeholder="ornek@mail.com" />
                  </Field>
                  <Field label="Telefon" testId="contact-phone">
                    <input value={form.phone} onChange={update("phone")} className="input-dark" placeholder="+90 ___ ___ __ __" />
                  </Field>
                  <Field label="Konu" testId="contact-subject">
                    <input value={form.subject} onChange={update("subject")} className="input-dark" placeholder="Örn: Sunucu kiralama" />
                  </Field>
                </div>

                <Field label="Mesajınız *" testId="contact-message" className="mt-5">
                  <textarea required rows={6} value={form.message} onChange={update("message")} className="input-dark resize-y" placeholder="Projeniz veya ihtiyacınız hakkında kısa bir not..." />
                </Field>

                {/* Cevap rozeti + KVKK metni — solda, Mesaj Gönder butonu sağda aynı hizada */}
                <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <div className="space-y-2 max-w-md">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#34C759]/30 bg-[#34C759]/10 px-3 py-1 text-[11px] font-semibold text-[#4ADE80]" data-testid="contact-response-badge">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#34C759]"></span>
                      </span>
                      Genellikle 30 dakika içinde dönüyoruz
                    </div>
                    <div className="text-xs text-white/50">
                      Mesajınızı göndererek <a href="/kvkk" className="underline hover:text-[#FF3B30]">KVKK Aydınlatma Metni</a>'ni okuduğunuzu kabul etmiş sayılırsınız.
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={status.state === "loading" || (siteKey && !turnstileToken)}
                    data-testid="contact-submit"
                    className="self-end sm:self-auto inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status.state === "loading" ? "Gönderiliyor..." : <>Mesaj Gönder <Send size={16}/></>}
                  </button>
                </div>

                {/* Turnstile — bilgi+buton satırının altında */}
                {siteKey && (
                  <div className="mt-6" data-testid="contact-turnstile">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={siteKey}
                      onSuccess={(token) => setTurnstileToken(token)}
                      onError={() => setTurnstileToken("")}
                      onExpire={() => setTurnstileToken("")}
                      options={{ theme: "dark", language: "tr" }}
                    />
                  </div>
                )}

                {status.state === "success" && (
                  <div data-testid="contact-success" className="mt-6 flex items-start gap-3 rounded-2xl bg-[#34C759]/10 border border-[#34C759]/30 p-5 text-[#4ADE80]">
                    <CheckCircle2 className="flex-none mt-0.5" /> <span className="text-sm font-medium">{status.msg}</span>
                  </div>
                )}
                {status.state === "error" && (
                  <div data-testid="contact-error" className="mt-6 flex items-start gap-3 rounded-2xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-5 text-[#FF6B63]">
                    <AlertCircle className="flex-none mt-0.5" /> <span className="text-sm font-medium">{status.msg}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .input-dark {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 14px;
          padding: 14px 16px;
          color: #FFFFFF;
          font-size: 15px;
          font-family: inherit;
          transition: border-color .2s, background .2s;
        }
        .input-dark:focus {
          outline: none;
          border-color: #FF3B30;
          background: rgba(255,255,255,0.06);
        }
        .input-dark::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </>
  );
}

function InfoCard({ icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4 rounded-3xl bg-white/[0.03] border border-white/10 p-7 hover:border-[#FF3B30]/50 hover:bg-white/[0.06] transition-all">
      <div className="flex-none w-12 h-12 rounded-2xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center text-[#FF3B30]">{icon}</div>
      <div>
        <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-white/50">{label}</div>
        <div className="mt-1.5 text-white font-semibold leading-snug">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{content}</a> : content;
}

function Field({ label, children, className = "", testId }) {
  return (
    <label className={`block ${className}`} data-testid={testId}>
      <span className="block text-xs font-bold tracking-[0.2em] uppercase text-white/50 mb-2">{label}</span>
      {children}
    </label>
  );
}
